import { createContext, useContext, useId, type ReactNode } from "react";

import { cn } from "../lib/cn";

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  baseId: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error(`<${component}> has to be rendered inside <Tabs>.`);
  }
  return context;
}

/** Tab values become part of DOM ids, so they get squeezed into id-safe text. */
function idPart(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]+/g, "-");
}

function tabId(baseId: string, value: string): string {
  return `${baseId}-tab-${idPart(value)}`;
}

function panelId(baseId: string, value: string): string {
  return `${baseId}-panel-${idPart(value)}`;
}

export interface TabsProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> {
  /** Value of the selected tab. Controlled. */
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * A set of manila folders in a drawer: the selected tab is the folder pulled
 * open, its panel the sheet inside; the rest stay filed behind it. Controlled
 * root: provides the selected value and the shared id namespace to `TabList`,
 * `Tab` and `TabPanel`.
 *
 * Native props land on the wrapper the folders are stacked in.
 */
export function Tabs({
  value,
  onValueChange,
  children,
  className,
  ...rest
}: TabsProps) {
  const baseId = useId();

  return (
    <TabsContext.Provider value={{ value, onValueChange, baseId }}>
      <div {...rest} className={cn("flex flex-col", className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export interface TabListProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "role" | "onKeyDown"
> {
  children: ReactNode;
  /** Names the tab set for screen readers. */
  "aria-label"?: string;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The row of folder tabs. Sits on top of a hairline rule that the open folder's
 * tab breaks through, and owns the APG arrow-key navigation.
 *
 * Native props land on the tablist element. `role` and the arrow-key handler
 * are not among them - they are the widget, not defaults to swap out.
 */
export function TabList({
  children,
  "aria-label": ariaLabel,
  className,
  ...rest
}: TabListProps) {
  const { onValueChange } = useTabsContext("TabList");

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight" &&
      event.key !== "Home" &&
      event.key !== "End"
    ) {
      return;
    }

    const tabs = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        '[role="tab"]:not([disabled])',
      ),
    );
    if (tabs.length === 0) return;

    const current = tabs.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex: number;
    switch (event.key) {
      case "ArrowLeft":
        nextIndex = current <= 0 ? tabs.length - 1 : current - 1;
        break;
      case "ArrowRight":
        nextIndex = current === tabs.length - 1 ? 0 : current + 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      default:
        nextIndex = tabs.length - 1;
    }

    const next = tabs[nextIndex];
    if (!next) return;

    event.preventDefault();
    next.focus();
    // Activation follows focus (the panels here are cheap to render).
    if (next.dataset.value !== undefined) {
      onValueChange(next.dataset.value);
    }
  }

  return (
    <div
      {...rest}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn("flex items-end gap-1 border-b border-rule", className)}
    >
      {children}
    </div>
  );
}

/*
 * Manila is not a token, it is a mix: card stock soaked in the gold quill
 * color, mixed exactly as FolderTab mixes it (see FolderTab.tsx) so a tab set
 * and a patient folder are cut from the same sheet. `tab` is the doubled-over
 * edge that stands above the sheet, so it carries more gold than `body`, the
 * sheet itself; `filed` is a tab still behind the open folder, barely lifted
 * off the page. All three live in one constant so the tab fills and the panel
 * surface cannot drift apart.
 *
 * The mixes are whole class strings rather than a percentage plugged into a
 * template: Tailwind finds classes by scanning source text, so anything built
 * at runtime emits no CSS.
 */
const MANILA = {
  tab: "bg-[color-mix(in_srgb,var(--color-gold)_28%,var(--color-card))]",
  body: "bg-[color-mix(in_srgb,var(--color-gold)_18%,var(--color-card))]",
  filed: "bg-[color-mix(in_srgb,var(--color-gold)_12%,var(--color-card))]",
} as const;

/*
 * The tab is a trapezoid cut with clip-path: same polygon as FolderTab's
 * manila tab (see FolderTab.tsx) - one folder-tab shape language across the
 * kit. Sides slant ~14deg from vertical, top corners softened by a small
 * bevel, wider at the base where the tab meets the panel below.
 *
 * clip-path clips away a CSS border along with everything else the element
 * paints, so the hairline outline is layered instead of drawn with `border`:
 * the outer element IS the trapezoid, painted in the rule color, and an
 * inner element repeats the same polygon 1px smaller in the tab's fill
 * color. The inner layer is inset on the sides and top only - it runs flush
 * to the very bottom - so no rule-colored stroke ever crosses the fold where
 * a tab meets the rule below it. That is what lets the selected tab's manila
 * run straight into the panel's, folded rather than cut.
 */
const TAB_SHAPE =
  "[clip-path:polygon(0_100%,9px_15%,14px_0,calc(100%_-_14px)_0,calc(100%_-_9px)_15%,100%_100%)]";

export interface TabProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children" | "role" | "type" | "value" | "onClick"
> {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * One folder tab, cut as a trapezoid: angled sides, wider at the base where it
 * meets the panel. The selected tab is manila at full strength and pulls its
 * bottom edge over the tablist rule so it merges with the sheet below; the
 * others are the same manila washed out, sitting a hair lower - folders still
 * filed behind the open one.
 *
 * clip-path clips outlines away along with the rest of a clipped element's
 * paint, so the focus ring cannot live on the trapezoid layers themselves -
 * it would be sliced off at the slanted sides. It lives instead on this
 * outer `<button>`, which carries no clip-path of its own (only its two
 * nested shape layers are clipped): the ring renders as a plain rectangle
 * around the trapezoid, entirely unclipped and always visible, regardless of
 * the cut shape drawn inside it.
 *
 * Native props land on that `<button>`. The tab's own identity - role, id,
 * `aria-selected`, the roving tabindex and the selection click - is not
 * overridable: it is what makes the tab set a tab set.
 */
export function Tab({
  value,
  children,
  disabled = false,
  className,
  ...rest
}: TabProps) {
  const { value: selected, onValueChange, baseId } = useTabsContext("Tab");
  const active = selected === value;

  return (
    <button
      {...rest}
      type="button"
      role="tab"
      id={tabId(baseId, value)}
      data-value={value}
      aria-selected={active}
      aria-controls={panelId(baseId, value)}
      tabIndex={active ? 0 : -1}
      disabled={disabled}
      onClick={() => onValueChange(value)}
      className={cn(
        "-mb-px min-w-0 rounded-paper",
        "font-display text-sm",
        "transition duration-150 ease-paper",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        "disabled:cursor-not-allowed disabled:text-faint",
        active ? "relative z-10 text-ink" : "translate-y-px text-annotation",
        className,
      )}
    >
      <span className={cn(TAB_SHAPE, "relative block bg-rule")}>
        <span
          aria-hidden="true"
          className={cn(
            TAB_SHAPE,
            "absolute inset-x-px top-px bottom-0 block",
            active ? MANILA.tab : MANILA.filed,
          )}
        />
        <span className="relative block min-w-0 truncate px-6 py-2">
          {children}
        </span>
      </span>
    </button>
  );
}

export interface TabPanelProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "role" | "hidden"
> {
  value: string;
  children: ReactNode;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The open folder under the tabs. Stays mounted and hidden when its tab is not
 * selected; its manila surface is what the selected tab folds into.
 *
 * Native props land on the panel element. `hidden` is not one of them - the
 * selected value decides which folder is open.
 */
export function TabPanel({
  value,
  children,
  className,
  ...rest
}: TabPanelProps) {
  const { value: selected, baseId } = useTabsContext("TabPanel");
  const active = selected === value;

  return (
    <div
      {...rest}
      role="tabpanel"
      id={panelId(baseId, value)}
      aria-labelledby={tabId(baseId, value)}
      hidden={!active}
      tabIndex={0}
      className={cn(
        MANILA.body,
        "border border-t-0 border-rule p-4",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        className,
      )}
    >
      {children}
    </div>
  );
}
